import { BaseQueryFn, FetchArgs } from "@reduxjs/toolkit/query";
// import { format } from "date-fns";
import moment from "moment";
import { Platform } from "react-native";
import { RootState } from "../store";
import { resetAll } from "../store/actions/resetAll";
import { authActions } from "../store/slices/auth";
import { GenericResponse } from "../types";
import { isAccountLockedError, showToast } from "../utils";
import { decryptString, encryptString } from "../utils/encryption";

const TIMEOUT = 30000;

/** Max consecutive failures per endpoint before we stop calling the server (prevents refetch loops). */
const MAX_CONSECUTIVE_FAILURES = 5;
/** After this many ms, the failure count for an endpoint is reset so the user can try again. */
const FAILURE_COOLDOWN_MS = 60_000;

type FailureEntry = { count: number; lastFailureAt: number };
const failureTracker: Record<string, FailureEntry> = {};

let _fetch = fetch;
let BASE_URL = "/dev-api/";
let APP_HEADER = "Web";
let ENCRYPT_VERSION = "1.0";
let APP_ENV = "DEV";
let isEncryptionEnabled = false;
let sslPinningOptions = {};
let logEvent:
  | ((name: "API_ERROR" | "MOBILE_CTA" | "WEB_CTA", params?: Record<string, any>) => void)
  | undefined = undefined;

type ExtraOptions = {
  useTemporaryToken?: boolean;
  xApiToken?: string;
  skipEncryption?: boolean;
  skipErrorToast?: boolean;
  /**
   * Suppress the error toast only when the failure is an account-locked error.
   * The OTP verification screen owns the locked UX (Account Locked modal), so it
   * opts in to avoid a duplicate toast while other errors still surface as toasts.
   */
  skipAccountLockedToast?: boolean;
};

interface Request extends Omit<any, ""> {
  url?: string;
  data?: Record<string, string>;
}

export function initialiseAPI({
  baseUrl,
  applicationType,
  env,
  enableEncryption,
  fetchApi,
  sslOptions,
  eventLogger,
}: {
  baseUrl: string;
  env: "DEV" | "PROD";
  applicationType?: "Web" | "Mobile";
  enableEncryption?: boolean;
  fetchApi?: any;
  sslOptions?: any;
  eventLogger?: (
    name: "API_ERROR" | "MOBILE_CTA" | "WEB_CTA",
    params?: Record<string, any>,
  ) => void;
}) {
  if (baseUrl) BASE_URL = applicationType === "Web" ? "/web-api/" : baseUrl;
  if (applicationType === "Web") APP_HEADER = applicationType;
  if (env) APP_ENV = env;
  if (typeof enableEncryption === "boolean") isEncryptionEnabled = enableEncryption;
  if (fetchApi) _fetch = fetchApi;
  if (sslOptions) sslPinningOptions = sslOptions;
  if (eventLogger) logEvent = eventLogger;
  // console.log(`Initializing API with baseURL: ${BASE_URL}`);
}

async function getParsedResponse(response: Response, options: Request) {
  try {
    // ✅ If response is a standard fetch response
    if (response?.text) {
      const resString = await response.text();

      try {
        // Response comes as { "data": "<IV>:<encrypted_base64>" }
        // Parse the outer JSON first, then decrypt the inner `data` value
        const parsedWrapper = JSON.parse(resString);

        if (
          isEncryptionEnabled &&
          !options.skipEncryption &&
          parsedWrapper?.data &&
          typeof parsedWrapper.data === "string"
        ) {
          const decryptedText = decryptString(parsedWrapper.data);
          return JSON.parse(decryptedText);
        }

        // If no `data` key, return parsed JSON as-is (unencrypted response)
        return parsedWrapper;
      } catch (e) {
        // If all parsing/decryption fails, return the raw string
        return resString;
      }
    }

    const fallbackRaw = response?.toString();
    try {
      return JSON.parse(fallbackRaw);
    } catch (e) {
      return fallbackRaw;
    }
  } catch (err) {
    return { message: "Failed to parse response", error: String(err) };
  }
}

const onRequest = (config: Request) => {
  //! Error: Failed to load the boundary parameter

  config.headers?.["Content-Type"]?.includes("form-data")
    ? delete config.headers["Content-Type"]
    : (config.headers["Content-Type"] = "application/json");
  config.headers["AppVersion"] = "0.0.1";
  config.headers["AppHeader"] = APP_HEADER;
  config.headers["encrypt-version"] = ENCRYPT_VERSION;

  if (config.method === "GET") {
    delete config.body;
  }

  return {
    ...config,
    headers: config.headers,
  };
};

const onResponseFulFilled = async (response: Response, request: Request) => {
  if (response?.status >= 400) {
    throw response;
  }

  const data = response ? await getParsedResponse(response, request) : response;

  return {
    ...response,
    data: data,
  };
};

const apiClient = async ({
  url,
  options,
  normalizedBody,
}: {
  url: string;
  options?: Request;
  normalizedBody: any;
}) => {
  const modifiedOptions = onRequest({ ...options });

  let _BASE_URL = BASE_URL;

  if (options?.url) {
    _BASE_URL = options?.url;
  }

  const conditionalFetch = _fetch;
  const response = await conditionalFetch(`${_BASE_URL}${url}`, {
    ...(modifiedOptions as any),
    timeoutInterval: TIMEOUT,
    pkPinning: true,
    sslPinning: sslPinningOptions,
    json: true,
  });
  const parsedResponse = await onResponseFulFilled(response, modifiedOptions);

  if (APP_ENV === "DEV") {
    console.log("****************************************************************");
    console.log(`Request URL   => ${response.url}`);
    // console.log("Time stamp =>", format(new Date(), "dd-MM-yyyy HH:mm:ss"));
    console.log(`Request Body  =>`, options?.body);
    console.log(`Request Body (normalized) =>`, normalizedBody);
    try {
      const responseData = (await parsedResponse)?.data;
      const stringified = JSON.stringify(responseData, null, " ");
      const MAX_LOG_LENGTH = 10000;
      console.log(
        `Response Body =>`,
        stringified.length > MAX_LOG_LENGTH
          ? stringified.slice(0, MAX_LOG_LENGTH) +
              `\n... [truncated, total ${stringified.length} chars]`
          : stringified,
      );
    } catch (logErr) {
      console.log(`Response Body => [could not stringify: ${String(logErr)}]`);
    }
    console.log("****************************************************************");
  }

  return parsedResponse;
};

const normalizeJointHolderBody = (url: string, body: any) => {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return body;
  }

  const isJointHolderMutation =
    url.includes("profile/jointholder-create") ||
    url.includes("profile/jointholder-update");

  if (!isJointHolderMutation) {
    return body;
  }

  const { pan, ...rest } = body;
  return {
    ...rest,
    jtpan: body.jtpan || pan,
  };
};

const axiosBaseQuery: BaseQueryFn<
  FetchArgs & ExtraOptions,
  GenericResponse<any>,
  GenericResponse<any> | (any & { response: { error: { message: string } } })
> = async ({ url, method, body, headers, params, ...extraOptions }, api) => {
  headers = headers as any;
  const { token, temporaryToken, guestToken } = (api.getState() as RootState).user;
  const isGuestLogin = !!guestToken;

  const {
    useTemporaryToken = false,
    xApiToken = "",
    skipEncryption = false,
    skipErrorToast = false,
    skipAccountLockedToast = false,
  } = extraOptions ?? {};

  if (params) {
    const queryString = Object.keys(params)
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(params[key] as string)}`,
      )
      .join("&");
    url = url.includes("?") ? `${url}&${queryString}` : `${url}?${queryString}`;
  }

  const authHeader: {
    Authorization?: string;
    "x-api-token"?: string;
    "guest-token"?: string;
  } = isGuestLogin
    ? {
        Authorization: `Bearer ${guestToken}`,
      }
    : {
        Authorization: `Bearer ${useTemporaryToken ? temporaryToken : token}`,
        ...(xApiToken ? { "x-api-token": xApiToken } : {}),
      };

  const normalizedBody = normalizeJointHolderBody(url, body);
  const requestMethod = (method || "GET").toUpperCase();
  const shouldHaveRequestBody = requestMethod !== "GET" && requestMethod !== "HEAD";

  let finalBody: any = normalizedBody;
  if (shouldHaveRequestBody && (finalBody === undefined || finalBody === null)) {
    // Android SSL-pinning rejects POST/PUT/PATCH requests without a body.
    // Default to an empty JSON object so all non-GET requests have a payload.
    finalBody = {};
  }

  if (
    !(headers as any)?.["Content-Type"] &&
    shouldHaveRequestBody &&
    finalBody !== undefined &&
    finalBody !== null
  ) {
    if (isEncryptionEnabled && !skipEncryption) {
      finalBody = JSON.stringify({
        data: encryptString(
          JSON.stringify({
            ...finalBody,
          }),
        ),
      });
    } else {
      finalBody = JSON.stringify({ ...finalBody });
    }
  }

  const options: Request = {
    method,
    body: finalBody,
    skipEncryption,
    headers: {
      ...authHeader,
      ...headers,
    },
  };

  const requestKey = `${method}:${url}`;
  const now = Date.now();
  const entry = failureTracker[requestKey];
  if (entry) {
    if (now - entry.lastFailureAt >= FAILURE_COOLDOWN_MS) {
      delete failureTracker[requestKey];
    } else if (entry.count >= MAX_CONSECUTIVE_FAILURES) {
      return {
        error: {
          status: 429,
          url,
          message: "Too many failed attempts. Please try again later.",
          data: null,
        },
      };
    }
  }

  try {
    const response = await apiClient({ url, options, normalizedBody });

    delete failureTracker[requestKey];
    return { data: response.data };
  } catch (errorResponse: any) {
    const errorResponseData = errorResponse
      ? await getParsedResponse(errorResponse, options)
      : errorResponse;

    // An expired/invalid OTP during login/signup returns 401 but is NOT a session
    // expiry — resetting state here would wipe the user's email/mobile from the OTP
    // screen and mask the real "OTP has expired" message. Skip the reset for these.
    const isOtpError =
      errorResponseData?.code === "OTP_EXPIRED" ||
      errorResponseData?.code === "INVALID_OTP" ||
      (errorResponseData?.message?.toLowerCase()?.includes("otp") ?? false);

    if (errorResponse?.status === 401 && !isGuestLogin && !isOtpError) {
      // const isCugError = errorResponseData?.message?.toLowerCase().includes("cug");
      // if (!isCugError) {
      if (Platform.OS === "web") {
        api.dispatch(resetAll());
      } else {
        // Mobile: only clear auth flag so user goes to WelcomeMpinScreen (MPIN),
        // preserving PAN/token/mpin/deviceId state.
        api.dispatch(authActions.setIsAuthenticated(false));
      }
      // }
    }

    if (APP_ENV === "DEV") {
      console.error("==============ERROR==============");
      console.error(`Request URL   => ${errorResponse?.url || url}`);
      console.error(`Request Method => ${method}`);
      // console.error("API Request Timestamp", format(new Date(), "dd-MM-yyyy HH:mm:ss"));
      console.error("API Request Token", authHeader.Authorization);
      console.error(`Status  =>`, errorResponse?.status);
      console.error(`Request Body  =>`, normalizedBody);
      console.error(`Response Body => `, errorResponseData);
      console.error("==============ERROR==============");
    }

    let errorMessage;
    switch (errorResponse?.status) {
      case 500:
        errorMessage = "Server Error";
        break;
      case 503:
        errorMessage = "Service unavailable";
        break;
      default:
        errorMessage = "Unknown Error, Please try later";
    }
    const finalErrorMessage: string = errorResponseData?.message || errorMessage;

    failureTracker[requestKey] = {
      count: (failureTracker[requestKey]?.count ?? 0) + 1,
      lastFailureAt: Date.now(),
    };

    const suppressLockedToast =
      skipAccountLockedToast &&
      isAccountLockedError({ status: errorResponse?.status, data: errorResponseData });
    if (!skipErrorToast && !suppressLockedToast) {
      showToast("error", finalErrorMessage);
    }

    logEvent?.("API_ERROR", {
      url: errorResponse?.url?.split("dev-api")?.[1],
      status: errorResponse?.status,
      message: finalErrorMessage || "-",
      request: typeof body === "string" ? body : JSON.stringify(body),
      response:
        typeof errorResponseData === "string"
          ? errorResponseData
          : JSON.stringify(errorResponseData),
      timestamp: moment(new Date()).format("DD-MM-YYYY HH:mm:ss"),
    });

    return {
      error: {
        status: errorResponse?.status ?? 0,
        url: errorResponse?.url ?? url,
        message: finalErrorMessage,
        data: errorResponseData, // parsed JSON (serializable)
      },
    };
  }
};

export { apiClient, axiosBaseQuery };
